import React from 'react';
import { Gym } from '../types';
import { Star, ImageOff, Heart, Loader2 } from 'lucide-react';
import { getGymThumbnailUrl, getGymPhotoSrcSet, buildGymAltText } from '../lib/photos';
import { DEFAULT_STATE_DISPLAY } from '../lib/stateRoutes';
import { Card } from '../src/components/ui/card';

interface GymCardProps {
  gym: Gym;
  /**
   * Canonical gym-profile URL for this card. Rendered as the anchor's `href`
   * so the browser provides native new-tab affordances (Cmd/Ctrl-click,
   * middle-click, right-click "Open in new tab", URL preview on hover).
   */
  href: string;
  /**
   * Plain left-click handler — runs the SPA fast-path (seeds state + router
   * navigation). Modified/middle clicks are NOT intercepted; they fall through
   * to the browser so it can open a new tab. `onClick` only fires for an
   * unmodified primary click.
   */
  onClick: () => void;
  isHighlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isFavourite?: boolean;
  onFavouriteToggle?: () => void;
  isFavouriteLoading?: boolean;
  /** Position in the visible list. The first card (index 0) is the LCP image and
   *  the top row (index < 4) is eager-loaded; all others stay lazy. */
  index?: number;
}

const GymCard: React.FC<GymCardProps> = ({
  gym,
  href,
  onClick,
  isHighlighted = false,
  onMouseEnter,
  onMouseLeave,
  isFavourite = false,
  onFavouriteToggle,
  isFavouriteLoading = false,
  index
}) => {
  const [imgError, setImgError] = React.useState(false);
  const effectivePlaceId = gym.google_place_id || (gym.id?.startsWith('ChIJ') ? gym.id : null);
  const thumbnailUrl = effectivePlaceId ? getGymThumbnailUrl(effectivePlaceId) : null;
  // DL-8: eager-load the first row (index < 4) so above-the-fold cards don't wait
  // on lazy-loading; the first card (index 0) is the LCP image (fetchPriority high).
  const eager = index !== undefined && index < 4;

  const handleFavouriteClick = (e: React.MouseEvent) => {
    // Inside the card but NOT inside the anchor; still stop bubbling so a
    // favourite toggle never triggers card navigation.
    e.preventDefault();
    e.stopPropagation();
    onFavouriteToggle?.();
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Let the browser handle new-tab / new-window intents natively:
    //  - modifier keys (Cmd/Ctrl/Shift/Alt)
    //  - middle-click / any non-primary button
    // For a plain primary click, take over with the SPA fast-path.
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    onClick();
  };

  return (
    // Real <a href> so native new-tab behaviours work. The favourite button is
    // a SIBLING overlay (not nested) — a <button> inside an <a> is invalid HTML.
    // Card supplies bg-surface-base / border / rounded-2xl / overflow-hidden;
    // p-0 cancels the default variant padding and transition-all duration-300
    // overrides Card's transition-colors duration-150 (twMerge) so the rendered
    // result is byte-identical to the previous raw div.
    <Card
      className={`p-0 relative flex flex-col h-full transition-all duration-300 group ${isHighlighted
        ? 'border-brand-primary ring-1 ring-focus-ring'
        : 'border-border-default hover:border-brand-primary/50'
        }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <a
        href={href}
        onClick={handleCardClick}
        className="flex flex-col h-full cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-brand-primary rounded-2xl"
      >
        {/* Image Section */}
        <div className="relative aspect-[2/1] bg-surface-canvas overflow-hidden">
          {thumbnailUrl && !imgError ? (
            <img
              src={thumbnailUrl}
              srcSet={effectivePlaceId ? getGymPhotoSrcSet(effectivePlaceId, 0) : undefined}
              sizes="(min-width:1024px) 25vw, (min-width:640px) 33vw, 100vw"
              alt={buildGymAltText(gym)}
              /* UI-126 (spec RULED B5) — `bg-brand-skeleton` is the PENDING tone. It
                 paints immediately and the image covers it on load (object-cover always
                 fills the box), so a photo still arriving reads as pending instead of
                 sharing the MISSING branch's `bg-surface-canvas`. ⚠️ STATIC BY RULING:
                 do NOT swap this for the shared `Skeleton` primitive — that primitive is
                 `animate-pulse bg-brand-skeleton`, and the operator ruled a static tone
                 over a pulse. Reuse its COLOUR, never its animation. */
              className="w-full h-full object-cover bg-brand-skeleton transition-transform duration-300 group-hover:scale-105"
              loading={eager ? 'eager' : 'lazy'}
              fetchPriority={index === 0 ? 'high' : 'auto'}
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <ImageOff className="w-10 h-10 text-text-muted" strokeWidth={1.5} />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-3 flex flex-col justify-start h-[70px]">
          <div className="flex-1 min-w-0 w-full">
            <h3 className="text-base font-semibold text-text-primary line-clamp-1">
              {gym.name}
            </h3>
            <div className="flex items-end justify-between mt-0.5">
              <p className="text-sm text-text-muted truncate">
                {gym.suburb} {gym.state || DEFAULT_STATE_DISPLAY} {gym.postcode}
              </p>
              {gym.google_rating && (
                <div className="flex items-center text-sm text-text-primary flex-shrink-0 ml-2">
                  <Star size={16} className="text-state-warning fill-state-warning mr-1" strokeWidth={1.5} />
                  <span className="font-bold text-text-primary text-base leading-none">{gym.google_rating.toFixed(1)}</span>
                  <span className="text-text-muted text-sm leading-none ml-1 whitespace-nowrap">({gym.google_user_ratings_total?.toLocaleString()})</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </a>

      {/* Favourite Button — sibling overlay, outside the anchor (valid HTML).
          UI-13: kept as a raw <button> (justified) — bespoke transparent photo-overlay
          control (40px per DF4, 30px glyph); Button variant=icon/size=icon would need
          negations for its border, hover surface, radius, transition and [&>svg] 18px
          sizing, leaving nothing of the primitive in effect. */}
      {onFavouriteToggle && (
        <button
          aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
          aria-pressed={isFavourite}
          onClick={handleFavouriteClick}
          disabled={isFavouriteLoading}
          className="absolute top-2 right-2 w-10 h-10 rounded-full bg-transparent transition-all duration-200 flex items-center justify-center disabled:pointer-events-none disabled:opacity-60 group/fav z-10"
        >
          {isFavouriteLoading ? (
            <Loader2 size={30} className="animate-spin text-white" />
          ) : (
            <Heart
              size={30}
              strokeWidth={2}
              className={`stroke-white ${isFavourite ? 'fill-state-danger group-hover/fav:fill-state-danger-subtle' : 'fill-text-muted group-hover/fav:fill-state-danger'}`}
            />
          )}
        </button>
      )}
    </Card>
  );
};

// Memoised: the directory/favourites grids re-render on every hover/highlight
// change. With stable (useCallback) handlers from the parents, memo skips
// re-rendering cards whose props are unchanged (DF3).
export default React.memo(GymCard);
